import { useState, useEffect, useMemo } from "react";
import { useSearchConfig } from "../hooks/useSearchConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Building, Send, CircleCheck } from "lucide-react";
import { Vision360Map } from "../components/Vision360Map";
import { Vision360Card } from "../components/Vision360Card";
import { Vision360Drawer } from "../components/Vision360Drawer";
import { Card } from "../types";
import { Skeleton } from "@/components/ui/skeleton";
import { SelectionProvider } from "../contexts/SelectionContext";
import { apiVision360 } from "@/services/api-vision360";

function ShortListContent() {
  const { config, saveConfig } = useSearchConfig();
  const navigate = useNavigate();
  const [cards, setCards] = useState<Card[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [drawerCard, setDrawerCard] = useState<Card | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const loadCards = async () => {
      try {
        const shortListId = new URLSearchParams(location.search).get("s_id");
        if (!shortListId) {
          setIsLoading(false);
          return;
        }

        const response = await apiVision360.get(
          `/api/v1/shortlists/detail/${shortListId}`,
        );
        const data = response.data;

        saveConfig({
          dealId: data?.deal_id,
          dealName: data?.client_name,
        });

        const selectedCardsData = data?.buildings;
        const convertedCards: Card[] = selectedCardsData.map(
          (building: any) => ({
            id: building.id,
            search_id: building.search_id,
            title: building.title || building.name || building.id,
            address: building.address || building.location?.address || "",
            neighborhood: building.neighborhood,
            cityname: building.cityname || building.city,
            capacity: building.capacity || 0,
            monthly_price: building.monthly_price || building.monthlyPrice || 0,
            credits: building.credits || 0,
            images: building.images || [],
            features: building.features || {
              total_area: building.total_area || 0,
              available_area: building.available_area || 0,
              floor_area: building.floor_area || 0,
              floors: building.floors || 0,
              elevators: building.elevators || 0,
              parking_spots: building.parking_spots || 0,
              ceiling_height: building.ceiling_height || 0,
              floor_load: building.floor_load || 0,
              building_class: building.building_class || "",
              leed_certification: building.leed_certification || "",
              has_generator: building.has_generator || false,
              has_air_conditioning: building.has_air_conditioning || false,
              has_raised_floor: building.has_raised_floor || false,
              has_auditorium: building.has_auditorium || false,
              has_helipad: building.has_helipad || false,
              has_cctv: building.has_cctv || false,
              has_access_control: building.has_access_control || false,
              has_24h_security: building.has_24h_security || false,
            },
            pricing: building.pricing || {
              base_rent_per_sqm: building.base_rent_per_sqm || 0,
              condo_fee_per_sqm: building.condo_fee_per_sqm || 0,
              iptu_per_sqm: building.iptu_per_sqm || 0,
              total_cost_per_sqm: building.total_cost_per_sqm || 0,
              total_monthly_cost: building.total_monthly_cost || 0,
              cost_per_workstation: building.cost_per_workstation || 0,
              estimated_workstations: building.estimated_workstations || 0,
              woba_efficiency_factor: building.woba_efficiency_factor || 0,
              final_price_with_woba: building.final_price_with_woba || 0,
              currency: building.currency || "BRL",
              contract_duration_months: building.contract_duration_months || 12,
              last_updated: building.last_updated || new Date().toISOString(),
            },
            original_url: building.original_url || "",
            whatsapp_number: building.whatsapp_number,
            phone: building.phone,
            size: building.size,
            type: building.type,
            origin: building.origin,
            source: building?.source || null,
            external_url: building.external_url,
            status: building.status || 0,
            updated_at: building.updated_at || new Date().toISOString(),
            space: {
              name:
                building.space?.name ||
                building.space_name ||
                building.spaceName ||
                "Unknown Space",
              id: String(
                building.space?.id ||
                  building.space_id ||
                  building.spaceId ||
                  building.id,
              ),
            },
            verified: building?.source === "bts_list",
            metadata: building.metadata || null,
            description: building.description || null,
            coords: {
              lat:
                building.coords?.lat ||
                building.latitude ||
                building.location?.latitude ||
                0,
              lng:
                building.coords?.lng ||
                building.longitude ||
                building.location?.longitude ||
                0,
            },
          }),
        );

        setCards(convertedCards);
      } catch (error) {
        setCards([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadCards();
  }, []);

  const handleBack = () => {
    if (config?.longListId && config?.dealId) {
      navigate(
        `${DEFAULT_ROUTES.VISION_360_BETA_LONG_LIST}?l_id=${config?.longListId}`,
      );
    } else {
      navigate(DEFAULT_ROUTES.VISION_360_BETA_SETUP);
    }
  };

  const handleSubmitToSupply = async () => {
    setIsSubmitting(true);

    await new Promise((resolve) => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setShowSuccess(true);
  };

  const memoizedCards = useMemo(() => cards, [cards]);

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Short List</h1>
        </div>
        <div className="space-y-4">
          <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
            <span className="text-gray-400">Carregando mapa...</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="border rounded-lg bg-white shadow-sm overflow-hidden flex flex-col h-full"
              >
                <div className="relative h-40 w-full flex-shrink-0">
                  <Skeleton className="w-full h-full" />
                </div>
                <div className="p-4 flex flex-col flex-1 min-h-0">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2 mb-4" />
                  <Skeleton className="h-8 w-1/3 mb-2" />
                  <Skeleton className="h-4 w-2/3 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="p-4">
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="flex flex-col justify-start items-center gap-4 inline-flex">
            <CircleCheck className="h-16 w-16" />
            <div className="flex-col justify-start items-center gap-px inline-flex">
              <div className="text-center text-[#08080a] text-2xl font-semibold font-['Inter'] leading-loose">
                Sucesso!
              </div>
              <div className="text-center text-zinc-500 text-sm font-normal font-['Inter'] leading-tight mb-4">
                Sua seleção foi encaminhada para o time de Supply com sucesso!
              </div>
              <Button
                className="border border-[#e3e3e7] justify-center items-center gap-2 inline-flex hover:bg-gray-900 group cursor-pointer"
                onClick={() => navigate(DEFAULT_ROUTES.VISION_360)}
              >
                <Building className="h-4 w-4 group-hover:text-white transition-colors duration-200" />
                <label className="group-hover:text-white transition-colors duration-200 cursor-pointer">
                  Retornar ao início
                </label>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center gap-4 mb-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBack}
          className="h-8 w-8"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Short List</h1>
      </div>

      <div className="space-y-6">
        <div className="h-96 bg-gray-100 rounded-lg overflow-hidden">
          <Vision360Map cards={memoizedCards} onOpenDrawer={setDrawerCard} />
        </div>

        {memoizedCards.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Building className="h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhum imóvel selecionado
            </h3>
            <p className="text-gray-500">
              Volte à etapa anterior para selecionar ativos.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-20 h-full">
              {memoizedCards.map((card) => (
                <Vision360Card
                  key={card.id}
                  card={card}
                  onOpenDrawer={setDrawerCard}
                  showSelectButton={false}
                />
              ))}
            </div>

            <div className="fixed left-0 right-0 bottom-0 z-40 bg-white border-t py-4 px-6 flex items-center justify-between">
              <div className="text-left">
                <h2 className="text-lg font-bold text-gray-900">
                  Pronto para avançar?
                </h2>
                <p className="text-gray-600 text-sm">
                  Encaminhe esta seleção para o time de Supply para prosseguir
                  com o processo.
                </p>
              </div>

              <Button
                onClick={handleSubmitToSupply}
                disabled={isSubmitting}
                className="bg-primary text-white px-6 py-2 rounded flex items-center gap-3 hover:bg-primary/90 transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Encaminhar para Supply
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </div>

      <Vision360Drawer
        open={!!drawerCard}
        card={drawerCard}
        onClose={() => setDrawerCard(null)}
        showSelectButton={false}
      />
    </div>
  );
}

export default function ShortListLayout() {
  return (
    <SelectionProvider
      initialSelection={new Set()}
      onSelectionChange={() => {}}
    >
      <ShortListContent />
    </SelectionProvider>
  );
}
